import React, { useState, useEffect } from 'react';
import { dbRealtime } from '../firebase';
import { ref, get as dbGet, set as dbSet, update as dbUpdate, remove as dbRemove } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeKey, stripAccents } from '../utils/helpers';
import { getMappingsArray, invalidateCache } from '../utils/mappingCache';

function Database() {
  const { user, userDoc } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({ description: '', glAccount: '' });
  const [message, setMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [resolvingIndex, setResolvingIndex] = useState(null);
  const [resolvingChoice, setResolvingChoice] = useState({});
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const itemsPerPage = 30;

  // Group mappings by first letter of the original description
  const groupedMappings = mappings.reduce((acc, mapping) => {
    let first = mapping.description?.[0]?.toUpperCase() || '#';
    if (!/^[A-Z]$/.test(first)) first = '#';
    if (!acc[first]) acc[first] = [];
    acc[first].push(mapping);
    return acc;
  }, {});
  const availableLetters = Object.keys(groupedMappings).sort((a, b) => {
    if (a === '#') return 1;
    if (b === '#') return -1;
    return a.localeCompare(b);
  });
  // Default to first available letter if selectedLetter is not present
  useEffect(() => {
    if (!availableLetters.includes(selectedLetter) && availableLetters.length > 0) {
      setSelectedLetter(availableLetters[0]);
    }
  }, [availableLetters, selectedLetter]);
  // Reset to first page when group or search changes
  useEffect(() => { setCurrentPage(1); }, [selectedLetter, searchTerm]);

  // Filter and paginate within selected group using original description
  const group = groupedMappings[selectedLetter] || [];
  let filteredMappings;
  if (searchTerm.trim() === '') {
    filteredMappings = group;
  } else {
    filteredMappings = mappings.filter(item =>
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.glAccount.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  const totalPages = Math.ceil(filteredMappings.length / itemsPerPage);
  const paginatedMappings = filteredMappings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const loadMappings = async (forceRefresh = false) => {
    setLoading(true);
    try {
      if (forceRefresh) await invalidateCache();
      const mappingsData = await getMappingsArray();
      setMappings(mappingsData);
      return mappingsData;
    } catch (error) {
      console.error('Error loading mappings:', error);
      setMessage('Failed to load mappings.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
  }, []);

  const handleEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditingData({ description: mapping.description, glAccount: mapping.glAccount });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({ description: '', glAccount: '' });
  };

  const handleUpdate = async (id) => {
    if (!editingData.description || !editingData.glAccount) {
      setMessage('Description and GL Account cannot be empty.');
      return;
    }
    if (editingData.description.trim().length < 5) {
      setMessage('Description must be at least 5 non-space characters.');
      return;
    }
    try {
      // Create new sanitized key for the updated description
      const newSanitizedKey = sanitizeKey(editingData.description);
      // Remove old key if description changed
      if (id !== newSanitizedKey) {
        await dbRemove(ref(dbRealtime, `gl_mappings/${id}`));
      }
      // Store in new format: sanitized key with object containing description and glAccount
      await dbSet(ref(dbRealtime, `gl_mappings/${newSanitizedKey}`), {
        description: editingData.description,
        glAccount: editingData.glAccount
      });
      setMessage('Mapping updated successfully.');
      setEditingId(null);
      loadMappings(true);
    } catch (error) {
      console.error('Error updating mapping:', error);
      if (error.code === 'PERMISSION_DENIED') {
        if (userDoc && userDoc.admin && user) {
          setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
        } else {
          setMessage('Permission denied. You need admin privileges to modify the database.');
        }
      } else {
        setMessage('Failed to update mapping: ' + error.message);
      }
    } finally {
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dbRemove(ref(dbRealtime, `gl_mappings/${id}`));
      setMessage('Mapping deleted successfully.');
      loadMappings(true);
    } catch (error) {
      console.error('Error deleting mapping:', error);
      if (error.code === 'PERMISSION_DENIED') {
        if (userDoc && userDoc.admin && user) {
          setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
        } else {
          setMessage('Permission denied. You need admin privileges to delete mappings.');
        }
      } else {
        setMessage('Failed to delete mapping: ' + error.message);
      }
    } finally {
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleMultiDelete = async () => {
    if (selectedIds.size === 0) {
      setMessage('No mappings selected to delete.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} selected mappings? This action cannot be undone.`)) {
      setLoading(true);
      try {
        const deletePromises = Array.from(selectedIds).map(id => dbRemove(ref(dbRealtime, `gl_mappings/${id}`)));
        await Promise.all(deletePromises);
        setMessage(`${selectedIds.size} mappings deleted successfully.`);
        setSelectedIds(new Set()); // Clear selection
        loadMappings(true);
      } catch (error) {
        console.error('Error deleting multiple mappings:', error);
        if (error.code === 'PERMISSION_DENIED') {
          if (userDoc && userDoc.admin && user) {
            setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
          } else {
            setMessage('Permission denied. You need admin privileges to delete mappings.');
          }
        } else {
          setMessage('Failed to delete selected mappings: ' + error.message);
        }
      } finally {
        setLoading(false);
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleSelect = (id) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allVisibleIds = filteredMappings.map(m => m.id);
      setSelectedIds(new Set(allVisibleIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  /**
   * Extract word-based n-grams from a normalized description.
   * Returns n-grams of 1, 2, and 3 consecutive words.
   */
  const extractNgrams = (text) => {
    const words = text.split(/\s+/).filter(w => w.length > 0);
    const ngrams = new Set();
    for (let n = 1; n <= Math.min(3, words.length); n++) {
      for (let i = 0; i <= words.length - n; i++) {
        const gram = words.slice(i, i + n).join(' ');
        if (gram.length >= 5) ngrams.add(gram);
      }
    }
    return ngrams;
  };

  /**
   * Greedy set cover: find multiple keywords that together cover all descriptions
   * in a GL account group. Each keyword must cover ≥ 3 descriptions and not conflict
   * with other GL accounts.
   */
  const findCoverKeywords = (descriptions, otherDescriptions) => {
    // Build n-gram frequency map
    const ngramToIndices = {};
    descriptions.forEach((desc, idx) => {
      const ngrams = extractNgrams(desc);
      for (const gram of ngrams) {
        if (!ngramToIndices[gram]) ngramToIndices[gram] = new Set();
        ngramToIndices[gram].add(idx);
      }
    });

    // Filter out n-grams that conflict with other GL accounts
    const nonConflicting = Object.entries(ngramToIndices).filter(([gram]) =>
      !otherDescriptions.some(desc => desc.includes(gram))
    );

    // Greedy set cover
    const selectedKeywords = [];
    const covered = new Set();

    while (covered.size < descriptions.length) {
      let best = null;
      let bestCoverage = [];

      for (const [gram, idxSet] of nonConflicting) {
        const uncoveredHits = [...idxSet].filter(i => !covered.has(i));
        if (
          uncoveredHits.length > bestCoverage.length ||
          (uncoveredHits.length === bestCoverage.length && gram.length > (best ? best.length : 0))
        ) {
          best = gram;
          bestCoverage = uncoveredHits;
        }
      }

      if (!best || bestCoverage.length < 3) break; // Stop if best covers fewer than 3

      selectedKeywords.push({
        keyword: best.toUpperCase(),
        coveredCount: bestCoverage.length,
      });
      bestCoverage.forEach(i => covered.add(i));
    }

    return { keywords: selectedKeywords, coveredTotal: covered.size };
  };

  const handleAnalyzeKeywords = async () => {
    setIsAnalyzing(true);
    setMessage('Analyzing keywords and removing duplicates... this may take a moment.');
    setAnalysisResults(null);

    const results = [];
    let duplicatesRemoved = 0;

    // --- Step 1: Remove duplicates (accent-normalized, case-insensitive) ---
    const duplicateGroups = {};
    mappings.forEach(mapping => {
      const key = `${stripAccents(mapping.description.toLowerCase())}_${mapping.glAccount}`;
      if (!duplicateGroups[key]) duplicateGroups[key] = [];
      duplicateGroups[key].push(mapping);
    });

    const duplicateIdsToDelete = [];
    Object.keys(duplicateGroups).forEach(key => {
      const group = duplicateGroups[key];
      if (group.length > 1) {
        group.slice(1).forEach(mapping => duplicateIdsToDelete.push(mapping.id));
        duplicatesRemoved += group.length - 1;
      }
    });

    if (duplicateIdsToDelete.length > 0) {
      try {
        await Promise.all(duplicateIdsToDelete.map(id => dbRemove(ref(dbRealtime, `gl_mappings/${id}`))));
        setMessage(`Removed ${duplicatesRemoved} duplicate keywords. Now analyzing for consolidation opportunities...`);
      } catch (error) {
        console.error('Error removing duplicates:', error);
        if (error.code === 'PERMISSION_DENIED') {
          if (userDoc && userDoc.admin && user) {
            setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node.`);
          } else {
            setMessage('Permission denied. You need admin privileges to modify the database.');
          }
        } else {
          setMessage('Error removing duplicates: ' + error.message);
        }
        setIsAnalyzing(false);
        return;
      }
    }

    // --- Step 2: Reload fresh data (fix stale state bug) ---
    const freshMappings = await loadMappings(true);

    // --- Step 3: Group by GL account and find consolidation opportunities ---
    const groups = freshMappings.reduce((acc, mapping) => {
      const { glAccount, description, id } = mapping;
      if (!acc[glAccount]) acc[glAccount] = [];
      acc[glAccount].push({ description, id });
      return acc;
    }, {});

    for (const glAccount in groups) {
      const keywords = groups[glAccount];
      if (keywords.length < 3) continue; // Lowered threshold from 10 to 3

      // Normalize descriptions for analysis
      const descriptions = keywords.map(k => stripAccents(k.description.toLowerCase().replace(/[^a-z0-9 ]/g, ' ')));

      // Collect descriptions from OTHER GL accounts for conflict detection
      const otherDescriptions = freshMappings
        .filter(m => m.glAccount !== glAccount)
        .map(m => stripAccents(m.description.toLowerCase().replace(/[^a-z0-9 ]/g, ' ')));

      const { keywords: coverKeywords, coveredTotal } = findCoverKeywords(descriptions, otherDescriptions);

      if (coverKeywords.length > 0) {
        // Check for conflicts on each keyword against all other GL accounts
        for (const kw of coverKeywords) {
          let conflictingMapping = null;
          for (const mapping of freshMappings) {
            if (
              stripAccents(mapping.description.toLowerCase()).includes(kw.keyword.toLowerCase()) &&
              mapping.glAccount !== glAccount
            ) {
              conflictingMapping = mapping;
              break;
            }
          }

          if (conflictingMapping) {
            results.push({
              status: 'CONFLICT',
              keyword: kw.keyword,
              glAccount,
              conflict: conflictingMapping,
              originalCount: keywords.length,
              coveredCount: kw.coveredCount,
            });
          } else {
            results.push({
              status: 'OK',
              keyword: kw.keyword,
              glAccount,
              originalCount: keywords.length,
              coveredCount: kw.coveredCount,
              idsToDelete: keywords.map(k => k.id),
            });
          }
        }
      }
    }

    setAnalysisResults(results);
    if (duplicatesRemoved > 0) {
      setMessage(`Analysis complete. Removed ${duplicatesRemoved} duplicates. ${results.length > 0 ? `Found ${results.length} consolidation opportunities.` : 'No consolidation opportunities found.'}`);
    } else {
      setMessage(results.length > 0 ? `Analysis complete. Found ${results.length} consolidation opportunities.` : 'No potential optimizations found.');
    }
    setIsAnalyzing(false);
  };

  const handlePerformConsolidation = async () => {
    const validConsolidations = analysisResults.filter(r => r.status === 'OK');

    if (validConsolidations.length === 0) {
      setMessage("No valid consolidations to perform.");
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const totalToDelete = validConsolidations.reduce((sum, item) => sum + item.idsToDelete.length, 0);
    const totalNewKeywords = validConsolidations.length;

    if (window.confirm(`This will replace ${totalToDelete} keywords with ${totalNewKeywords} new consolidated keywords. Proceed?`)) {
      setIsAnalyzing(true);
      setMessage("Performing consolidation...");
      try {
        // Delete old mappings
        const deletePromises = validConsolidations.flatMap(item => item.idsToDelete.map(id => dbRemove(ref(dbRealtime, `gl_mappings/${id}`))));
        await Promise.all(deletePromises);
        // Add new consolidated mappings
        const addPromises = validConsolidations.map(item => dbSet(ref(dbRealtime, `gl_mappings/${sanitizeKey(item.keyword)}`), {
          description: item.keyword,
          glAccount: item.glAccount
        }));
        await Promise.all(addPromises);
        setMessage(`Keywords consolidated successfully! ${totalNewKeywords} new keywords created.`);
        setAnalysisResults(null); // Clear analysis
        loadMappings(true);
      } catch (error) {
        console.error('Error during consolidation:', error);
        if (error.code === 'PERMISSION_DENIED') {
          if (userDoc && userDoc.admin && user) {
            setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
          } else {
            setMessage('Permission denied. You need admin privileges to perform consolidation.');
          }
        } else {
          setMessage('An error occurred during consolidation: ' + error.message);
        }
      } finally {
        setIsAnalyzing(false);
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleResolveConflict = async (result, index, chosenGlAccount) => {
    setIsAnalyzing(true);
    setMessage('Resolving conflict and consolidating...');
    try {
      // Delete all old mappings for both GL accounts with the keyword
      const deletePromises = mappings.filter(mapping =>
        mapping.description.toLowerCase().includes(result.keyword.toLowerCase()) &&
        (mapping.glAccount === result.glAccount || mapping.glAccount === result.conflict.glAccount)
      ).map(mapping => dbRemove(ref(dbRealtime, `gl_mappings/${mapping.id}`)));
      await Promise.all(deletePromises);
      // Add the new consolidated mapping with the chosen GL account
      await dbSet(ref(dbRealtime, `gl_mappings/${sanitizeKey(result.keyword)}`), {
        description: result.keyword,
        glAccount: chosenGlAccount
      });
      setMessage(`Conflict resolved: "${result.keyword}" now mapped to "${chosenGlAccount}".`);
      setAnalysisResults(null);
      loadMappings(true);
    } catch (error) {
      console.error(error);
      if (error.code === 'PERMISSION_DENIED') {
        if (userDoc && userDoc.admin && user) {
          setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
        } else {
          setMessage('Permission denied. You need admin privileges to resolve conflicts.');
        }
      } else {
        setMessage('Error resolving conflict: ' + error.message);
      }
    } finally {
      setIsAnalyzing(false);
      setResolvingIndex(null);
    }
  };

  const handleDeleteAll = async () => {
    if (mappings.length === 0) {
      setMessage('Database is already empty.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!confirmDeleteAll) {
      setConfirmDeleteAll(true);
      setMessage(`⚠️ WARNING: This will delete ALL ${mappings.length} mappings. Click "Delete All Database" again to confirm.`);
      // Auto-cancel after 5 seconds
      setTimeout(() => {
        setConfirmDeleteAll(false);
        setMessage('');
      }, 5000);
      return;
    }

    // Second click — actually delete
    setConfirmDeleteAll(false);
    setLoading(true);
    try {
      await dbRemove(ref(dbRealtime, 'gl_mappings'));
      setMessage('All mappings deleted successfully.');
      setSelectedIds(new Set());
      setAnalysisResults(null);
      loadMappings(true);
    } catch (error) {
      console.error('Error deleting all mappings:', error);
      if (error.code === 'PERMISSION_DENIED') {
        if (userDoc && userDoc.admin && user) {
          setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
        } else {
          setMessage('Permission denied. You need admin privileges to delete the database.');
        }
      } else {
        setMessage('Failed to delete all mappings: ' + error.message);
      }
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{
          fontWeight: 700,
          fontSize: '2rem',
          color: '#fff',
          margin: 0,
          letterSpacing: '0.01em',
        }}>
          Manage GL Mappings
        </h2>
        <div style={{
          fontSize: '1rem',
          color: '#fff',
          marginTop: '0.25rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
        }}>
          View, edit, or delete the keywords used for classification
        </div>
      </div>

      {message && (
        <div className={`alert ${message.includes('Failed') ? 'alert-error' : 'alert-success'}`} style={{ transition: 'all 0.3s ease' }}>
          {message}
        </div>
      )}

      <div className="card">
        {/* A-Z navigation bar */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          marginBottom: '1.5rem',
          justifyContent: 'center',
          background: '#f0f4ff',
          borderRadius: '12px',
          padding: '0.75rem 1rem',
          boxShadow: '0 2px 8px rgba(102,126,234,0.06)',
        }}>
          {availableLetters.map(letter => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              style={{
                background: selectedLetter === letter ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                color: selectedLetter === letter ? 'white' : '#667eea',
                border: '1.5px solid #667eea',
                borderRadius: '6px',
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '0.4rem 1.1rem',
                margin: '0.1rem 0.1rem',
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
                outline: 'none',
                boxShadow: selectedLetter === letter ? '0 4px 16px rgba(102, 126, 234, 0.15)' : 'none',
                letterSpacing: '0.03em',
                position: 'relative',
              }}
              onMouseOver={e => { if (selectedLetter !== letter) e.currentTarget.style.background = '#eaf0ff'; }}
              onMouseOut={e => { if (selectedLetter !== letter) e.currentTarget.style.background = 'white'; }}
            >
              {letter}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0, minWidth: '200px' }}>
            <input
              type="text"
              className="form-control"
              placeholder={`Search all mappings... (${mappings.length} total)`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {selectedIds.size > 0 && (
            <button className="btn btn-danger btn-sm" onClick={handleMultiDelete}>
              Delete Selected ({selectedIds.size})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={handleAnalyzeKeywords} disabled={loading || isAnalyzing}>
            {isAnalyzing ? 'Analyzing...' : 'Analyze Keywords'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadMappings} disabled={loading || isAnalyzing}>
            Refresh
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDeleteAll}
            disabled={loading || isAnalyzing || mappings.length === 0}
            title={mappings.length === 0 ? 'Database is empty' : 'Delete all mappings'}
          >
            Delete All Database
          </button>
        </div>

        {analysisResults && (
          <div className="card" style={{ marginBottom: '2rem', borderLeft: '4px solid #667eea' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: '#333' }}>Analysis Report</h3>
            </div>
            {analysisResults.length === 0 && <p>No potential optimizations found.</p>}
            {analysisResults.map((result, index) => (
              <div key={index} style={{
                padding: '1rem',
                marginTop: '1rem',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: result.status === 'OK' ? '#c3e6cb' : '#f5c6cb',
                background: result.status === 'OK' ? '#d4edda' : '#f8d7da',
              }}>
                <p><strong>Proposed Keyword:</strong> "{result.keyword}" for GL Account "<strong>{result.glAccount}</strong>"</p>
                <p>Covers {result.coveredCount} of {result.originalCount} existing keywords in this group.</p>
                {result.status === 'CONFLICT' && (
                  <div style={{ color: '#721c24', marginTop: '0.5rem' }}>
                    <strong>Conflict:</strong> This action is blocked because the keyword "{result.keyword}" was also found in "<em>{result.conflict.description}</em>", which is linked to a different GL Account: "<strong>{result.conflict.glAccount}</strong>".<br />
                    <span>Choose which GL Account to use for "{result.keyword}": </span>
                    <button className="btn btn-secondary" style={{ margin: '0 0.5rem' }} disabled={isAnalyzing && resolvingIndex === index} onClick={() => { setResolvingIndex(index); handleResolveConflict(result, index, result.glAccount); }}>Use "{result.glAccount}"</button>
                    <button className="btn btn-secondary" style={{ margin: '0 0.5rem' }} disabled={isAnalyzing && resolvingIndex === index} onClick={() => { setResolvingIndex(index); handleResolveConflict(result, index, result.conflict.glAccount); }}>Use "{result.conflict.glAccount}"</button>
                    {isAnalyzing && resolvingIndex === index && <span style={{ marginLeft: '1rem' }}>Resolving...</span>}
                  </div>
                )}
                {result.status === 'OK' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      className="btn"
                      disabled={isAnalyzing && resolvingIndex === index}
                      onClick={async () => {
                        setIsAnalyzing(true);
                        setResolvingIndex(index);
                        try {
                          // Delete old mappings
                          const deletePromises = result.idsToDelete.map(id => dbRemove(ref(dbRealtime, `gl_mappings/${id}`)));
                          await Promise.all(deletePromises);
                          // Add new consolidated mapping
                          await dbSet(ref(dbRealtime, `gl_mappings/${sanitizeKey(result.keyword)}`), {
                            description: result.keyword,
                            glAccount: result.glAccount
                          });
                          setMessage(`Consolidation approved: "${result.keyword}" now mapped to "${result.glAccount}".`);
                          // Remove this result from analysisResults
                          setAnalysisResults(prev => prev.filter((_, i) => i !== index));
                          loadMappings(true);
                        } catch (error) {
                          if (error.code === 'PERMISSION_DENIED') {
                            if (userDoc && userDoc.admin && user) {
                              setMessage(`Permission denied. Your UID (${user.uid}) needs to be added to the Realtime Database admins node. Please add it manually in Firebase Console.`);
                            } else {
                              setMessage('Permission denied. You need admin privileges to perform consolidation.');
                            }
                          } else {
                            setMessage('An error occurred during consolidation: ' + error.message);
                          }
                        } finally {
                          setIsAnalyzing(false);
                          setResolvingIndex(null);
                        }
                      }}
                    >
                      {isAnalyzing && resolvingIndex === index ? 'Processing...' : 'Approve'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <span className="loading" style={{ borderTopColor: '#667eea' }}></span>
            <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading mappings...</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'center', width: '5%' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={filteredMappings.length > 0 && selectedIds.size === filteredMappings.length}
                    />
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                    Keyword / Description
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                    GL Account
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedMappings.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #dee2e6', background: selectedIds.has(item.id) ? '#fffbe6' : 'transparent' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => handleSelect(item.id)}
                      />
                    </td>
                    {editingId === item.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editingData.description}
                            onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                            className="form-control"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editingData.glAccount}
                            onChange={(e) => setEditingData({ ...editingData, glAccount: e.target.value })}
                            className="form-control"
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(item.id)}>Save</button>
                          <button className="btn btn-secondary btn-sm" style={{ marginLeft: '0.5rem' }} onClick={handleCancel}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '0.75rem' }}>{item.description}</td>
                        <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>{item.glAccount}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn btn-link btn-link-primary" onClick={() => handleEdit(item)}>Edit</button>
                          <button className="btn btn-link btn-link-danger" onClick={() => handleDelete(item.id)}>Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMappings.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                {searchTerm ? 'No mappings found matching your search.' : 'No mappings found.'}
              </div>
            )}
            {/* Pagination controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1.5rem 0 0.5rem 0', gap: '0.5rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>Page {currentPage} of {totalPages}</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Database; 